import { Injectable } from '@angular/core';
import { IHotelQuoteDetail, IHotelQuoteDetails } from "libraries/service/resources/hotel/hotel-quotation";
import { IResortFeeDetail } from "libraries/service/resources/model/search/IBooking";
import { IQuoteSupplement, ITotal } from "libraries/hotel/models/quote";
import { Yii } from "libraries/interfaces/Yii";
import { LabelService } from "libraries/service/label/label.service";
import { XSellLocationEnum } from 'libraries/service/resources/booking/interfaces/IGetBookings';

export enum EPricePerRoomColNames {
    title = 'title',
    room = 'room',
    roomRetail = 'roomRetail',
    total = 'total',
    totalRetail = 'totalRetail',
}

export interface IPricePerRoomPrice {
    amount: number;
    currency: string;
    roomId?: string;
}

export interface IPricePerRoomRow {
    [EPricePerRoomColNames.title]: string;
    type ?: string;
    isHeader?: boolean;
    [EPricePerRoomColNames.total]: IPricePerRoomPrice;
    [EPricePerRoomColNames.totalRetail]?: IPricePerRoomPrice;
    [EPricePerRoomColNames.room]: IPricePerRoomPrice[];
}

export interface IPricePerRoomHeaderRoom {
    id: string;
    name: string;
}

export interface IPricePerRoomRowHeaders {
    [EPricePerRoomColNames.title]: string;
    [EPricePerRoomColNames.total]: string;
    [EPricePerRoomColNames.room]: IPricePerRoomHeaderRoom[];
}

@Injectable()
export class PricePerRoomMappingService {

    constructor(
        private readonly labelService: LabelService,
    ) {
    }

    public getColsNames(quoteDetails: IHotelQuoteDetails): string[] {

        return ['title', 'total', ...extractRoomIds(quoteDetails), 'rowEnd'];
    }

    public getRoomsIds(quoteDetails: IHotelQuoteDetails): string[] {
        return extractRoomIds(quoteDetails);
    }

    public getTotalRows(quoteDetails: IHotelQuoteDetails, supplement: IQuoteSupplement[], quoteTotal: ITotal, currency: string): IPricePerRoomRow[] {
        if (supplement && !Array.isArray(supplement)) {
            // tslint:disable-next-line:no-parameter-reassignment
            supplement = [supplement];
        }
        const rooms: string[] = extractRoomIds(quoteDetails);

        const total: IPricePerRoomRow = {
            isHeader: true,
            [EPricePerRoomColNames.title]: Yii.t('wui-shared-js', 'Total dû à la réservation'),
            [EPricePerRoomColNames.total]: {
                amount: Number(quoteTotal.cost[currency]),
                currency
            },

            [EPricePerRoomColNames.room]: rooms.map((roomId: string): IPricePerRoomPrice => {
                const room: IHotelQuoteDetail = findRoomById(quoteDetails, roomId);
                const roomSupplement: IQuoteSupplement = supplement ? findSupplementById(supplement, roomId) : null;
                return {
                    amount: (room.unit ? Number(room.unit.cost[currency]): 0) + (roomSupplement ? Number(roomSupplement.value.cost[currency]) : 0),
                    currency,
                    roomId
                };
            })
        };
        if(quoteTotal.retail){
            total[EPricePerRoomColNames.totalRetail]= {
                amount: Number(quoteTotal.retail[currency]),
                    currency
            };

            total[EPricePerRoomColNames.roomRetail]= rooms.map((roomId: string): IPricePerRoomPrice => {
                const room: IHotelQuoteDetail = findRoomById(quoteDetails, roomId);
                const roomSupplement: IQuoteSupplement = supplement ? findSupplementById(supplement, roomId) : null;
                return {
                    amount: (room.unit ? Number(room.unit.retail[currency]): 0) + (roomSupplement ? Number(roomSupplement.value.retail[currency]) : 0),
                    currency,
                    roomId
                };
            })
        }

        const totalSupplements: number = supplement ? supplement
            .reduce((totalSupplement: number, supplementItem: IQuoteSupplement) => {
                return totalSupplement + Number(supplementItem.value.cost[currency]);
            }, 0) : 0;



        const base: IPricePerRoomRow = {
            [EPricePerRoomColNames.title]: totalSupplements && supplement?.length ?
                Yii.t('wui-shared-js', 'Prix de base HT') :
                Yii.t('wui-shared-js', 'Prix de base TTC'),
            [EPricePerRoomColNames.total]: {
                amount: Number(quoteTotal.cost[currency]) - totalSupplements,
                currency
            },
            [EPricePerRoomColNames.room]: rooms.map((roomId: string): IPricePerRoomPrice => {
                const room: IHotelQuoteDetail = findRoomById(quoteDetails, roomId);
                return {
                    amount: room.unit ? Number(room.unit.cost[currency]) : null,
                    currency,
                    roomId
                };
            })
        };



        const fees: IPricePerRoomRow[] = totalSupplements && supplement?.length ? [{
            [EPricePerRoomColNames.title]: Yii.t('wui-shared-js', 'Taxes et frais'),
            type: 'fee',
            [EPricePerRoomColNames.total]: {
                amount: totalSupplements,
                currency
            },
            [EPricePerRoomColNames.room]: rooms.map((roomId: string): IPricePerRoomPrice => {
                const roomSupplement: IQuoteSupplement = supplement ? findSupplementById(supplement, roomId) : null;

                return {
                    amount: supplement ? Number(roomSupplement.value.cost[currency]) : 0,
                    currency,
                    roomId
                };
            })
        }] : [];

        if(quoteTotal.retail){
            const totalSupplementsRetail: number = supplement ? supplement
                .reduce((totalSupplement: number, supplementItem: IQuoteSupplement) => {
                    return totalSupplement + Number(supplementItem.value.retail[currency]);
                }, 0) : 0;
            base[EPricePerRoomColNames.totalRetail] = {
                amount: Number(quoteTotal.retail[currency]) - totalSupplementsRetail,
                currency
            };
            base[EPricePerRoomColNames.roomRetail]= rooms.map((roomId: string): IPricePerRoomPrice => {
                const room: IHotelQuoteDetail = findRoomById(quoteDetails, roomId);
                return {
                    amount: room.unit ? Number(room.unit.retail[currency]) : null,
                    currency,
                    roomId
                };
            })
        }
        console.log([total, base, ...fees])
        return [total, base, ...fees];
    }

    public getLocalRows(quoteDetails: IHotelQuoteDetails, resortFees: IResortFeeDetail[], currency: string): IPricePerRoomRow[] {

        if (resortFees && !Array.isArray(resortFees)) {
            // tslint:disable-next-line:no-parameter-reassignment
            resortFees = [resortFees];
        }

        const rooms: string[] = extractRoomIds(quoteDetails);
        const currentFeeCurrencies: string[] = resortFees && resortFees.length ?
            resortFees.reduce((currencies: string[], fee: IResortFeeDetail) => {
                return [
                    ...currencies,
                    ...Object.keys(fee.value.cost)
                ];
            }, []) : [currency];

        const currentFeeCurrency: string = currentFeeCurrencies.find((feeCurrency: string) => feeCurrency === currency) || currentFeeCurrencies[0];

        const totalFees: number = resortFees && resortFees.length ?
            resortFees
                .reduce((total: number, fee: IResortFeeDetail) => {
                    return total + Number(fee.total.cost[currentFeeCurrency]);
                }, 0) : 0;

        const hasLocalSellLocation: boolean = (resortFees || []).some((fee: IResortFeeDetail) =>
            fee.xSellLocation === XSellLocationEnum.local &&
            totalFees >= 0
        );
        const rows: IPricePerRoomRow[] = [];

        if (hasLocalSellLocation) {
            const totalLocal: IPricePerRoomRow = {
                isHeader: true,
                [EPricePerRoomColNames.title]: Yii.t('wui-shared-js', 'Total à payer sur place'),
                [EPricePerRoomColNames.total]: {
                    amount: totalFees,
                    currency: currentFeeCurrency
                },
                [EPricePerRoomColNames.room]: rooms.map((roomId: string) => {
                    const feeTotalForThisRoom: number = resortFees && resortFees.length ?
                        findFeesById(resortFees, roomId)
                            .reduce((totalRoom: number, fee: IResortFeeDetail) => {
                                return totalRoom + Number(fee.total.cost[currentFeeCurrency]);
                            }, 0) : 0;
                    return {
                        roomId,
                        currency: currentFeeCurrency,
                        amount: feeTotalForThisRoom
                    };
                })
            };

            rows.push(totalLocal);
        }

        const resortFeeCodes: string[] = resortFees && resortFees.length ?
            resortFees
                .reduce((codeList: string[], fee: IResortFeeDetail) => {
                    if (fee.details && !codeList.includes(fee.details.code)) {
                        codeList.push(fee.details.code);
                    }
                    return codeList;
                }, [])
            : [];

        const feeRows: IPricePerRoomRow[] = resortFeeCodes.length ?
            resortFeeCodes
                .map((code: string) => {
                    const feesForCode: IResortFeeDetail[] = resortFees.filter((fee: IResortFeeDetail) => fee.details.code === code);

                    const totalFee: number = feesForCode
                        .reduce((total: number, fee: IResortFeeDetail) => {
                            return total + Number(fee.total.cost[currentFeeCurrency]);
                        }, 0);

                    return {
                        [EPricePerRoomColNames.title]: resortFees[0].details.name || Yii.t('wui-shared-js', 'Taxes et frais divers'),
                        [EPricePerRoomColNames.total]: {
                            amount: totalFee,
                            currency: currentFeeCurrency
                        },
                        [EPricePerRoomColNames.room]: rooms
                            .map((roomId: string) => {
                                const roomFee: IResortFeeDetail = findFeeById(feesForCode, roomId);
                                const amount: number = Number(roomFee.total.cost[currentFeeCurrency]);

                                return {
                                    roomId,
                                    amount,
                                    currency: currentFeeCurrency
                                };
                            })
                    };
                })
                .filter((feeRow: IPricePerRoomRow) =>
                    feeRow.total.amount >= 0 ||
                    feeRow.room.some((room: IPricePerRoomPrice) => room.amount >= 0
                    ))
            : [];

        rows.push(...feeRows);

        return rows;
    }

    public getHeaders(quoteDetails: IHotelQuoteDetails): IPricePerRoomRowHeaders {
        const room: IPricePerRoomHeaderRoom[] = Object.values(quoteDetails)
            .sort(sortByRoomId)
            .map((quoteDetail: IHotelQuoteDetail, index: number) => ({id: quoteDetail.room.xId, name: this.computeName(index + 1)}));
        return {
            [EPricePerRoomColNames.title]: "",
            [EPricePerRoomColNames.total]: Yii.t('wui-shared-js', 'Total'),
            [EPricePerRoomColNames.room]: room
        };
    }

    private computeName(index: number): string {
        return this.labelService.completeLabel(
            Yii.t('wui-shared-js', 'Chambre %placeholder%'),
            index.toString(),
        );
    }
}

function sortByRoomId(prev: IHotelQuoteDetail, curr: IHotelQuoteDetail): number {
    const idA: string = prev.room.xId.toLowerCase();
    const idB: string = curr.room.xId.toLowerCase();
    if (idA < idB) {
        return -1;
    }
    if (idB > idA) {
        return 1;
    }
    return 0;
}

function extractRoomIds(quoteDetails: IHotelQuoteDetails): string[] {
    return Object.values(quoteDetails).map((detail: IHotelQuoteDetail) => detail.room.xId);
}

function findRoomById(quoteDetails: IHotelQuoteDetails, roomId: string): IHotelQuoteDetail {
    return Object.values(quoteDetails).find((detail: IHotelQuoteDetail) => detail.room.xId === roomId);
}

function findSupplementById(supplements: IQuoteSupplement[], roomId: string): IQuoteSupplement {
    return Object.values(supplements).find((supplement: IQuoteSupplement) => supplement.room === roomId);
}

function findFeesById(fees: IResortFeeDetail[], roomId: string): IResortFeeDetail[] {
    return Object.values(fees).filter((fee: IResortFeeDetail) => fee.room === roomId);
}

function findFeeById(fees: IResortFeeDetail[], roomId: string): IResortFeeDetail {
    return Object.values(fees).find((fee: IResortFeeDetail) => fee.room === roomId);
}
